import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Shield, ShieldCheck, Copy, ExternalLink, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { VerifactuData } from '@shared/types';

interface VerifactuCardProps {
  verifactu?: VerifactuData;
  facturaNumero: string;
  estado: string;
}

export function VerifactuCard({ verifactu, facturaNumero, estado }: VerifactuCardProps) {
  if (!verifactu) {
    return (
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-muted-foreground" />
            Verifactu
          </CardTitle>
          <CardDescription>
            {estado === 'Borrador'
              ? 'Esta factura se registrará en Verifactu al guardarla como "Emitida".'
              : 'Esta factura no tiene registro Verifactu.'}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`, {
      description: 'Se ha copiado al portapapeles'
    });
  };

  const formatDate = (isoDate: string) => {
    return new Date(isoDate).toLocaleString('es-ES', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  return (
    <Card className="border-green-200 bg-green-50/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-green-600" />
          Verifactu - Sistema de Integridad
        </CardTitle>
        <CardDescription>
          Esta factura está registrada en el sistema Verifactu y forma parte de la cadena de integridad
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Información Principal */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            {/* Número de Registro */}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Número de Registro</label>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-lg font-mono">
                  #{verifactu.numeroRegistro}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => copyToClipboard(verifactu.numeroRegistro.toString(), 'Número de registro')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Fecha de Registro */}
            <div>
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                <Clock className="h-4 w-4" />
                Fecha de Registro
              </label>
              <p className="text-sm mt-1">{formatDate(verifactu.fechaHoraRegistro)}</p>
            </div>

            {/* Versión */}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Versión Verifactu</label>
              <Badge variant="secondary" className="mt-1">v{verifactu.version}</Badge>
            </div>
          </div>

          {/* Código QR */}
          {verifactu.qrCode && (
            <div className="flex flex-col items-center justify-center p-4 bg-white rounded-lg border">
              <img
                src={verifactu.qrCode}
                alt="Código QR de verificación"
                className="w-48 h-48 object-contain aspect-square"
              />
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Escanea para verificar públicamente
              </p>
            </div>
          )}
        </div>

        {/* Hash e Integridad */}
        <div className="space-y-3 pt-4 border-t">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <h4 className="font-semibold">Cadena de Integridad</h4>
          </div>

          {/* Hash Actual */}
          <div>
            <label className="text-sm font-medium text-muted-foreground">Hash de Integridad (SHA-256)</label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 text-xs bg-muted p-2 rounded font-mono overflow-x-auto">
                {verifactu.hash}
              </code>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => copyToClipboard(verifactu.hash, 'Hash')}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Hash Anterior */}
          <div>
            <label className="text-sm font-medium text-muted-foreground">Hash Anterior (Enlace)</label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 text-xs bg-muted p-2 rounded font-mono overflow-x-auto text-muted-foreground">
                {verifactu.hashAnterior}
              </code>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => copyToClipboard(verifactu.hashAnterior, 'Hash anterior')}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* URL de Verificación */}
        {verifactu.urlVerificacion && (
          <div className="pt-4 border-t">
            <label className="text-sm font-medium text-muted-foreground">Verificación Pública</label>
            <div className="flex items-center gap-2 mt-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => window.open(verifactu.urlVerificacion, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Verificar en línea
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(verifactu.urlVerificacion!, 'URL de verificación')}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Esta URL permite verificar públicamente la autenticidad de la factura
            </p>
          </div>
        )}

        {/* Información Legal */}
        <div className="pt-4 border-t bg-muted/30 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
          <p className="text-xs text-muted-foreground">
            <strong className="font-semibold">Cumplimiento normativo:</strong> Esta factura cumple con la normativa Verifactu de la AEAT.
            Los datos están protegidos mediante hash criptográfico SHA-256 y forman parte de una cadena de bloques inmutable.
            Periodo de retención mínimo: 4 años según la legislación fiscal española.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
